import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";

interface DeleteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  itemName: string;
  onConfirm: () => Promise<void> | void;
  confirmButtonText?: string;
  isDestructive?: boolean;
}

export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  itemName,
  onConfirm,
  confirmButtonText = "Delete",
  isDestructive = true,
}: DeleteConfirmationDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error during deletion:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="flex flex-row items-center gap-3">
          <div className={`p-2 rounded-full ${isDestructive ? 'bg-red-100 dark:bg-red-900/20' : 'bg-orange-100 dark:bg-orange-900/20'}`}>
            <AlertTriangle className={`h-5 w-5 ${isDestructive ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`} />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-left">{title}</DialogTitle>
            <DialogDescription className="text-left mt-1">
              {description}
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="py-4">
          <div className="rounded-md bg-muted p-3">
            <p className="text-sm font-medium text-foreground">
              {itemName}
            </p>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            This action cannot be undone. This will permanently delete the item and remove all associated data.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant={isDestructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 